import React from 'react';
import { formatDateTime, formatCurrency, getStatusColor } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Printer } from 'lucide-react';

export const ViewBillDialog = ({
  viewDialogOpen, setViewDialogOpen, selectedBill
}) => (
  <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
    <DialogContent className="max-w-2xl printable-section">
      <DialogHeader className="no-print">
        <DialogTitle className="font-[Manrope] flex items-center justify-between">
          <span>Invoice Details</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => window.print()}
            className="no-print"
            data-testid="print-bill-btn"
          >
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
        </DialogTitle>
      </DialogHeader>

      {/* Print Header */}
      <div className="hidden print:block print-header">
        <h1>HMC Buddy - Invoice</h1>
        <p>Healventure Hospital</p>
      </div>
      {selectedBill && (
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="text-sm text-slate-500">Bill ID</p>
              <p className="font-mono font-medium">{selectedBill.bill_id}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Date</p>
              <p>{formatDateTime(selectedBill.created_at)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Patient</p>
              <p className="font-medium">{selectedBill.patient?.full_name}</p>
              <p className="text-xs text-slate-500">{selectedBill.patient?.patient_id}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Status</p>
              <Badge className={getStatusColor(selectedBill.status)}>{selectedBill.status}</Badge>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedBill.items?.map((item, i) => (
                <TableRow key={`${item.description}-${item.quantity}-${item.unit_price}`}>
                  <TableCell>{item.description}</TableCell>
                  <TableCell className="capitalize">{item.category}</TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-500">Subtotal</span>
              <span>{formatCurrency(selectedBill.subtotal)}</span>
            </div>
            {selectedBill.discount_amount > 0 && (
              <div className="flex justify-between text-emerald-600">
                <span>Discount ({selectedBill.discount_percent}%)</span>
                <span>-{formatCurrency(selectedBill.discount_amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-semibold border-t pt-2">
              <span>Total</span>
              <span>{formatCurrency(selectedBill.total_amount)}</span>
            </div>
            <div className="flex justify-between text-emerald-600">
              <span>Paid</span>
              <span>{formatCurrency(selectedBill.paid_amount || 0)}</span>
            </div>
            <div className="flex justify-between text-red-500 font-semibold">
              <span>Balance Due</span>
              <span>{formatCurrency(selectedBill.total_amount - (selectedBill.paid_amount || 0))}</span>
            </div>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
